import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { createHttpClient } from '../services/http';
import { createFixtureClient } from '../services/fixtureClient';
import { fixtures } from '../data/fixtures';
import useErrorNavigation from '../lib/composables/useErrorNavigation';
import { useErrorStore } from './useErrorStore';
import * as Sentry from '@sentry/vue';
import { router } from '../router';

export const useApiClientStore = defineStore('api-client', () => {
  const tenantId = ref<string | null>(null);

  // Recomputed whenever tenantId changes
  const client = computed(() => {
    if (!tenantId.value) return null;

    // Grab these inside the computed so the store instance exists
    const errorStore = useErrorStore();
    const errorNavigation = useErrorNavigation(router);

    // NB. all request errors are handled here to keep the rest of the app clean
    const clientOptions = {
      onError: async ({ response }) => {
        const next = response.errors; // this should be the array from http.ts
        errorStore.setErrors(next);

        // Navigate to the error page unless explicitly told not to.
        if (errorStore.errors[0] && errorStore.errors[0]?.redirectToError !== false) {
          await errorNavigation.navigateToError({
            message: 'Sorry, something went wrong.',
          });
        } else {
          // ENG-20636 `redirectToError === false` means the user isn't taken
          // to the error page, so Error.vue never fires its captureMessage —
          // the error would be silent. Surface it here as error-level so we
          // still get signal on suppressed-error paths.
          const first = errorStore.errors[0];
          Sentry.captureMessage(first?.code ?? 'createHttpClient onError (suppressed)', {
            level: 'error',
            tags: {
              error_type: 'suppressed_api_error',
            },
            contexts: {
              suppressedError: {
                code: first?.code,
                message: first?.message,
                errorCount: errorStore.errors.length,
              },
            },
          });
        }
      },
    };

    const httpClient = createHttpClient(tenantId.value, clientOptions);

    // The fixture seam. Fixtures stand in for endpoints that don't exist yet;
    // anything unfixtured falls through to the real client, so endpoints can
    // land one at a time. False in every deployed environment.
    if (import.meta.env.VITE_USE_FIXTURES === 'true') {
      return createFixtureClient(fixtures, httpClient, clientOptions);
    }

    return httpClient;
  });

  function setTenantId(id: string | null) {
    tenantId.value = id;
  }

  function getClient() {
    if (!client.value) {
      throw new Error('API client not initialised (tenant not set)');
    }
    return client.value;
  }

  return { tenantId, client, setTenantId, getClient };
});
